import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  NgZone,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { getUuid } from '@sibe/utilities/common';
import { HsHypersceneEngineConstants } from '@lib/core-ts/engine-new/constants/hyperscene-engine-constants.global';
import { HsRenderObjects } from '@lib/core-ts/engine-new/filtering/render-objects.class';
import { hsMakeDefaultLights } from '@lib/core-ts/engine-new/functions/make-default-lights.function';
import { HsPerspectiveCamera } from '@lib/core-ts/engine-new/objects/cameras/perspective-camera.class';
import { HsClippingPlane } from '@lib/core-ts/engine-new/objects/clipping/clipping-plane.class';
import { HsClippingStorage } from '@lib/core-ts/engine-new/storages/clipping-storage.class';
import { HsLightStorage } from '@lib/core-ts/engine-new/storages/light-storage.class';
import type { HsGeometry } from '@lib/core-ts/graphic-engine-new/geometry/geometry.interface';
import type { HsIndexedGeometry } from '@lib/core-ts/graphic-engine-new/geometry/indexed-geometry.class';
import type { HsRegularGeometry } from '@lib/core-ts/graphic-engine-new/geometry/regular-geometry.class';
import type { HsSpriteGeometry } from '@lib/core-ts/graphic-engine-new/geometry/sprite-geometry.class';
import { HsGraphics } from '@lib/core-ts/graphic-render-new/graphics.class';
import { HsDefaultMaterial } from '@lib/core-ts/graphic-render-new/materials/default-material.class';
import type { HsMaterial } from '@lib/core-ts/graphic-render-new/materials/material.class';
import { HsPointsMaterial } from '@lib/core-ts/graphic-render-new/materials/points-material.class';
import { HsTextureMaterial } from '@lib/core-ts/graphic-render-new/materials/texture-material.class';
import { HsGraphicCollection } from '@lib/core-ts/graphic-render-new/objects/graphic-collection.class';
import { HsGraphicLayer } from '@lib/core-ts/graphic-render-new/objects/graphic-layer.class';
import type { HsGraphicLines } from '@lib/core-ts/graphic-render-new/objects/graphic-lines.class';
import { HsGraphicObject } from '@lib/core-ts/graphic-render-new/objects/graphic-object.class';
import { HsGraphicPoints } from '@lib/core-ts/graphic-render-new/objects/graphic-points.class';
import { HsGraphicQueue } from '@lib/core-ts/graphic-render-new/objects/graphic-queue.class';
import { HsSpriteConstants } from '@lib/core-ts/graphic-render-new/objects/sprite-constants-class';
import { HsRaycast } from '@lib/core-ts/graphic-render-new/raycast.class';
import { HsRender } from '@lib/core-ts/graphic-render-new/render.class';
import type { HsDefaultShader } from '@lib/core-ts/graphic-render-new/shaders/classes/default-shader.class';
import { HsShaderData } from '@lib/core-ts/graphic-render-new/shaders/classes/shader-data.class';
import type { HsShader } from '@lib/core-ts/graphic-render-new/shaders/classes/shader.class';
import { HsShaderObjectType } from '@lib/core-ts/graphic-render-new/shaders/enums/shader-object-type.enum';
import { HsTextureUtils } from '@lib/core-ts/graphic-render-new/textures/texture-utils';
import { HsHypersceneMathUtils } from '@lib/core-ts/math-new/functions/hyperscene-math-utils.class';
import { HsColor } from '@lib/core-ts/math-new/objects/color.class';
import { HsMatrix3 } from '@lib/core-ts/math-new/objects/matrix3.class';
import { HsMatrix4 } from '@lib/core-ts/math-new/objects/matrix4.class';
import { HsVector2 } from '@lib/core-ts/math-new/objects/vector2.class';
import { HsVector3 } from '@lib/core-ts/math-new/objects/vector3.class';
import { HsBox } from '@lib/core-ts/math-new/shapes/box.class';
import type { HsHypersceneTypes } from '@lib/core-ts/math-new/types/hyperscene-types.type';
import { HsSubscription as HypersceneSubscription } from '@lib/core-ts/reactive-new/classes/subscription.class';
import type { HsSubscriberData } from '@lib/core-ts/reactive-new/types/subscriber-data.type';
import { HsCameraToolTypeControlAction } from '@lib/core-ts/tools-new/actions/camera-tool-type-control-action.class';
import { HsCameraToolType } from '@lib/core-ts/tools-new/actions/camera-tool-type.enum';
import { HsControlActionType } from '@lib/core-ts/tools-new/actions/control-action-type.enum';
import { HsKeyboardControlAction } from '@lib/core-ts/tools-new/actions/keyboard-control-action.class';
import type { HsControlActionsHandler } from '@lib/core-ts/tools-new/camera/control-actions-handler.class';
import { HsControlBox } from '@lib/core-ts/tools-new/complex-primitives/control-box.class';
import { HsRotationalAxes } from '@lib/core-ts/tools-new/complex-primitives/rotational-axes.class';
import type { HsGeometryData } from '@lib/core-ts/tools-new/geometry-utils/geometry-data.class';
import { hsIndexGeometry } from '@lib/core-ts/tools-new/geometry-utils/index-geometry.function';
import type { HsIndexedGeometryData } from '@lib/core-ts/tools-new/geometry-utils/indexed-geometry-data.class';
import { hsMakeGeometry } from '@lib/core-ts/tools-new/geometry-utils/make-geometry.function';
import { hsMakeIndexedGeometry } from '@lib/core-ts/tools-new/geometry-utils/make-indexed-geometry.function';
import { hsMakeTexturedIndexedGeometry } from '@lib/core-ts/tools-new/geometry-utils/make-textured-indexed-geometry.function';
import { hsMergeGeometries } from '@lib/core-ts/tools-new/geometry-utils/merge-geometries.function';
import type { HsRegularGeometryData } from '@lib/core-ts/tools-new/geometry-utils/regular-geometry-data.class';
import { HsGlobal } from '@lib/core-ts/tools-new/global';
import { HsBoxHelper } from '@lib/core-ts/tools-new/helpers/box-helper.class';
import type { HsOutputMessageObject } from '@lib/core-ts/tools-new/messages/output-message-object.class';
import { HsOutputMessageType } from '@lib/core-ts/tools-new/messages/output-message-type.enum';
import { HsBoxEmitter } from '@lib/core-ts/tools-new/particles/box-emitter.class';
import { HsGravityForce } from '@lib/core-ts/tools-new/particles/gravity-force.class';
import { HsParticleSystem } from '@lib/core-ts/tools-new/particles/particle-system.class';
import { HsSphereCollider } from '@lib/core-ts/tools-new/particles/sphere-collider.class';
import { HsSphereEmitter } from '@lib/core-ts/tools-new/particles/sphere-emitter.class';
import { HsWindForce } from '@lib/core-ts/tools-new/particles/wind-force.class';
import { hsGenerateCapsuleGeometryData } from '@lib/core-ts/tools-new/primitives/generate-capsule-geometry-data.function';
import { hsGenerateCubeGeometryData } from '@lib/core-ts/tools-new/primitives/generate-cube-geometry-data.function';
import { hsGenerateCylinderGeometryData } from '@lib/core-ts/tools-new/primitives/generate-cylinder-geometry-data.function';
import { hsGenerateIndexedPlaneGeometryData } from '@lib/core-ts/tools-new/primitives/generate-indexed-plane-geometry-data.function';
import { hsGenerateSphereGeometryData } from '@lib/core-ts/tools-new/primitives/generate-sphere-function';
import { hsGenerateTorusGeometryData } from '@lib/core-ts/tools-new/primitives/generate-torus-geometry-data.function';
import { HsPrimitives } from '@lib/core-ts/tools-new/primitives/primitives.class';
import { HsCanvasSize } from '@lib/core-ts/tools-new/utils/canvas-size.class';
import { Subscription, fromEvent } from 'rxjs';
import { subscribeOutsideAngular } from '../../functions/rxjs-operators/subscribe-outside-angular.operator';
import { HYPERSCENE_PACKAGE_NAME } from './constants/hyperscene-package.const';
import { HyperscenePackageState } from './models/hyperscene-package-state.interface';
import { HyperscenePackageAdapterService } from './services/hyperscene-package-adapter.service';
import { KEY_MAP } from './utils/key-map.global';
import { HS_DEFAULT_VIEWER_FILTER, HsHypersceneViewer } from '@lib/core-ts/viewer/hyperscene-viewer.class';
import { HsViewerInput } from '@lib/core-ts/viewer/runtime/viewer-input.class';
import type { HsViewerSurface } from '@lib/core-ts/viewer/runtime/viewer-surface.class';

const CAMERA_POSITION: HsHypersceneTypes.Array3 = [0, 0, 0];
const ROTATION_POINT_DISTANCE: number = 30;

const SMOKE_TEXTURE: string = '/assets/img/smoke.png';
const SNOW_TEXTURE: string = '/assets/img/snowflake-2.png';

@Component({
  selector: 'hyperscene-new',
  templateUrl: './hyperscene-new.component.html',
  styleUrls: ['./hyperscene-new.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class HypersceneNewDemo implements AfterViewInit, OnDestroy {
  public readonly downsampleMin: number = HsRender.minDownsampleRate;
  public readonly downsampleMax: number = HsRender.maxDownsampleRate;
  public fps: number = 0;
  public packageState: HyperscenePackageState = {
    health: 'fallback',
    packageName: HYPERSCENE_PACKAGE_NAME,
    packageVersion: null,
    message: 'Package check is pending.',
  };
  @ViewChild('canvasDocument', { static: true })
  private readonly canvasDocument: ElementRef<HTMLCanvasElement> | null = null;

  private viewer: HsHypersceneViewer | null = null;
  private viewerInput: HsViewerInput | null = null;
  private readonly handleWindowResize: () => void = this.resize.bind(this);
  private frame: number = 0;
  private time: number = performance.now();

  private readonly camera: HsPerspectiveCamera = new HsPerspectiveCamera(getUuid(), 'camera');
  private readonly cubeCamera: HsPerspectiveCamera = new HsPerspectiveCamera(getUuid(), 'cubeCamera');

  private readonly cameraPoint: HsVector3 = new HsVector3(400, 150, 400);
  private readonly observablePoint: HsVector3 = new HsVector3(0, 0, 0);

  private gl: WebGL2RenderingContext | null = null;

  private objectsLayer: HsGraphicLayer | null = null;
  private objectsQueue: HsGraphicQueue | null = null;

  private cubeLayer: HsGraphicLayer | null = null;
  private cubeQueue: HsGraphicQueue | null = null;

  private axisLayer: HsGraphicLayer | null = null;
  private axisQueue: HsGraphicQueue | null = null;

  private graphics: HsGraphics | null = null;
  private readonly raycast: HsRaycast = new HsRaycast();

  private canvasSize: HsCanvasSize | null = null;

  private controlCubeRenderObjects: HsRenderObjects<HsGraphicObject, HsGraphicLayer> | null = null;
  private axisRenderObjects: HsRenderObjects<HsGraphicObject, HsGraphicLayer> | null = null;

  public selectedCameraToolType: HsCameraToolType | null = null;
  private shiftHasPressed: boolean = false;
  private readonly currentPointerPosition: HsVector2 = HsVector2.makeZeroVector();

  private xRayMaterial: HsMaterial | null = null;
  private xRayMode: boolean = false;
  private readonly xRayObjects: HsGraphicObject[] = [];

  private particleSystems: HsParticleSystem[] = [];

  private readonly subscription: Subscription = new Subscription();
  private readonly hypersceneSubscription: HypersceneSubscription = new HypersceneSubscription();

  private readonly packageAdapter: HyperscenePackageAdapterService = new HyperscenePackageAdapterService();

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly ngZone: NgZone,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    this.subscription.add(this.handleKeydown());
    this.subscription.add(this.handleKeyup());

    this.hypersceneSubscription.add(this.handleOutputMessage());
  }

  public ngAfterViewInit(): void {
    const canvasElement: HTMLCanvasElement | undefined = this.canvasDocument?.nativeElement;
    if (canvasElement === undefined) {
      return;
    }
    this.initOptions(canvasElement);
    void this.checkHyperscenePackage().then(() => this.initScene());
  }

  public ngOnDestroy(): void {
    window.removeEventListener('resize', this.handleWindowResize);
    this.viewerInput?.dispose();
    this.viewer?.dispose();
  }

  public async checkHyperscenePackage(): Promise<void> {
    this.packageState = {
      ...this.packageState,
      message: 'Checking package availability...',
    };
    this.changeDetector.markForCheck();

    this.packageState = await this.packageAdapter.probePackage();
    this.changeDetector.markForCheck();
  }

  public setDownsample(value: number): void {
    if (
      this.gl === null ||
      this.canvasDocument === null ||
      this.objectsLayer === null ||
      this.cubeLayer === null ||
      this.axisLayer === null
    ) {
      return;
    }
    this.render.downsampleRate = value;
    const rect: DOMRect = this.canvasDocument.nativeElement.getBoundingClientRect();
    this.render.setRenderSize(this.gl, rect.width, rect.height, [this.objectsLayer]);
    this.setLayerSizes(rect.width / value, rect.height / value);
  }

  public async addAllObjects(): Promise<void> {
    if (
      this.gl === null ||
      this.graphics === null ||
      this.objectsQueue === null ||
      this.cubeQueue === null ||
      this.axisQueue === null
    ) {
      return;
    }

    if (this.packageState.health !== 'ready') {
      this.packageState = {
        ...this.packageState,
        health: 'fallback',
        message: 'Package is not ready. Run "Check @sibe/hyperscene" before loading objects.',
      };
      this.changeDetector.markForCheck();
      return;
    }

    this.particleSystems = [];
    this.distributedRendering = false;
    this.objectsQueue.clear(this.graphics);
    this.cameraPoint.set(8, 3, 8);
    this.observablePoint.set(0, 0, 0);
    this.camera.worldMatrix.setPosition(this.cameraPoint.values);
    const metalShaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
    const clippingStorage: HsClippingStorage = new HsClippingStorage();
    clippingStorage.clippingPlanes.push(new HsClippingPlane(1, 0, 0, 0));
    clippingStorage.clippingPlanes.push(new HsClippingPlane(0, 1, 0, 0));
    metalShaderData.setClippingStorage(clippingStorage);
    metalShaderData
      .addColor()
      .addDepth()
      .addNormals()
      .addRayIntersection()
      .addDiffuseLighting()
      .addBackgroundReflection()
      .addFresnel();
    const metalShader: HsShader = metalShaderData.makeShader();
    const metalMaterial: HsMaterial = new HsDefaultMaterial(this.gl, metalShader);
    metalMaterial.color.set(0.5, 0.5, 0.5);

    const linesShaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
    linesShaderData.addColor().addDepth();
    const linesShader: HsShader = linesShaderData.makeShader();
    const linesMaterial: HsMaterial = new HsDefaultMaterial(this.gl, linesShader);
    linesMaterial.color.set(1.0, 0.0, 0.0);

    const torusData: HsIndexedGeometryData = hsGenerateTorusGeometryData(1.2, 0.6, 12, 32);
    const torusGeometry: HsGeometry | null = hsMakeIndexedGeometry(this.graphics, torusData);
    if (torusGeometry !== null) {
      const torus: HsGraphicObject = new HsGraphicObject(getUuid(), 'torus', torusGeometry, metalMaterial);
      torus.worldMatrix.setPosition([4, 0, -2]);
      torus.updateBoundingSphere();
      this.objectsQueue.addObject(torus);
    }

    const primitives: HsPrimitives = new HsPrimitives(this.graphics);
    primitives.setQueue(this.objectsQueue);
    primitives.setMaterial(metalMaterial);
    primitives.makeCapsule([0, 0, -2], undefined, undefined, 20);
    primitives.makeCube([-4, 0, -2]);
    primitives.makeSphere([0, 0, -6], undefined, 20);
    primitives.makeCylinder([-4, 0, -6], undefined, undefined, 20);
    primitives.makePlane([4, 0, -6]);

    const phongShaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
    phongShaderData.addColor().addDepth().addNormals().addRayIntersection().addDiffuseLighting().addSpecularLighting();
    const phongShader: HsShader = phongShaderData.makeShader();
    const phongMaterial: HsMaterial = new HsDefaultMaterial(this.gl, phongShader);
    phongMaterial.color.set(1.0, 0.5, 0.2);
    if (torusGeometry !== null) {
      const torus: HsGraphicObject = new HsGraphicObject(getUuid(), 'torus', torusGeometry, phongMaterial);
      torus.worldMatrix.setPosition([4, 0, 2]);
      torus.updateBoundingSphere();
      this.objectsQueue.addObject(torus);
    }

    primitives.setMaterial(phongMaterial);
    primitives.makeCapsule([0, 0, 2], undefined, undefined, 20);
    primitives.makeCube([-4, 0, 2]);
    primitives.makeSphere([0, 10, -60], [2, 2, 2], 20);
    primitives.makeCylinder([-4, 0, 6], undefined, undefined, 20);
    primitives.makePlane([4, 0, 6]);

    new HsControlBox(this.graphics, this.cubeQueue);
    new HsRotationalAxes(this.graphics, this.axisQueue);

    const spherePointsGeometryData: HsGeometryData = hsGenerateSphereGeometryData(24);
    const spherePointsGeometry: HsRegularGeometry | null = hsMakeGeometry(this.graphics, spherePointsGeometryData);
    const pointsShaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
    pointsShaderData.addColor().addDepth().addPoints();
    const pointsShader: HsDefaultShader = pointsShaderData.makeShader();
    if (spherePointsGeometry !== null) {
      const spherePointsMaterial: HsPointsMaterial<HsDefaultShader> = new HsPointsMaterial(this.gl, pointsShader);
      spherePointsMaterial.color.set(0.2, 0.2, 0.2);
      const spherePoints: HsGraphicPoints = new HsGraphicPoints(
        getUuid(),
        'spherePoints',
        spherePointsGeometry,
        spherePointsMaterial
      );
      spherePoints.setPointsSize(this.gl, 30);
      spherePoints.worldMatrix.setPosition([4, 0, 10]);
      spherePoints.updateBoundingSphere();
      this.objectsQueue.addObject(spherePoints);

      const boxHelper: HsBoxHelper = new HsBoxHelper(this.graphics, HsBox.makeUnitCube(), HsColor.makeRed());
      const lines: HsGraphicLines = boxHelper.makeLines();
      lines.worldMatrix.setPosition([4, 0, 14]);
      lines.updateBoundingSphere();
      this.objectsQueue.addObject(lines);
    }
    const texturedPlainGeometryData: HsIndexedGeometryData = hsGenerateIndexedPlaneGeometryData();
    const texturedPlainGeometry: HsIndexedGeometry | null = hsMakeTexturedIndexedGeometry(
      this.graphics,
      texturedPlainGeometryData
    );
    const texture: WebGLTexture | null = await HsTextureUtils.load(this.gl, SMOKE_TEXTURE);
    const texturedPlainShaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
    texturedPlainShaderData.setLightStorage(new HsLightStorage(hsMakeDefaultLights(0, 5)));
    texturedPlainShaderData.addColor().addDepth().addTexture(0.1).addNormals().addDiffuseLighting();
    const texturedPlainShader: HsDefaultShader = texturedPlainShaderData.makeShader();
    const texturedPlainMaterial: HsTextureMaterial<HsDefaultShader> = new HsTextureMaterial(
      this.gl,
      texturedPlainShader
    );
    texturedPlainMaterial.color.set(1, 1, 1);
    texturedPlainMaterial.texture = texture;
    texturedPlainMaterial.getShader().hasTransparency = true;

    const snowTexture: WebGLTexture | null = await HsTextureUtils.load(this.gl, SNOW_TEXTURE);
    const snowShaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
    snowShaderData.setLightStorage(new HsLightStorage(hsMakeDefaultLights(0, 5)));
    snowShaderData.addColor().addDepth().addTexture().addNormals().addDiffuseLighting();
    const snowShader: HsDefaultShader = texturedPlainShaderData.makeShader();
    const snowMaterial: HsTextureMaterial<HsDefaultShader> = new HsTextureMaterial(this.gl, snowShader);
    snowMaterial.color.set(1, 1, 1);
    snowMaterial.texture = snowTexture;
    snowMaterial.getShader().hasTransparency = true;

    if (texturedPlainGeometry !== null && texture !== null) {
      const texturedPlain: HsGraphicObject = new HsGraphicObject(
        getUuid(),
        'texturedPlain',
        texturedPlainGeometry,
        texturedPlainMaterial
      );
      texturedPlain.worldMatrix.setPosition([0, 0, 10]);
      texturedPlain.updateBoundingSphere();
      this.objectsQueue.addObject(texturedPlain);
    }

    const particleGeometry: HsIndexedGeometry | null = hsMakeIndexedGeometry(
      this.graphics,
      hsIndexGeometry(hsGenerateSphereGeometryData(8))
    );

    if (particleGeometry === null) {
      return;
    }

    const spriteGeometry: HsSpriteGeometry | null = HsSpriteConstants.makeSpriteGeometry(this.graphics);

    if (spriteGeometry === null) {
      return;
    }

    const gravity: HsGravityForce = new HsGravityForce(0.0001);
    const antiGravity: HsGravityForce = new HsGravityForce(-0.0001);
    const wind: HsWindForce = new HsWindForce(HsVector3.makeUnitVectorY().negate(), 0.0001);

    const smokeParticles: HsParticleSystem = new HsParticleSystem();
    smokeParticles.setGeometry(spriteGeometry);
    smokeParticles.setMaterial(texturedPlainMaterial);
    smokeParticles.setQueue(this.objectsQueue);
    smokeParticles.setEmitter(new HsSphereEmitter(0, 0, 0, 0.5));
    smokeParticles.worldMatrix.setPosition([0, 0, -20]);
    smokeParticles.size = 0.5;
    smokeParticles.scattering = 0.02;
    smokeParticles.sizeVariation = 1;
    smokeParticles.setForces([antiGravity]);

    const smokeParticlesWithCollision: HsParticleSystem = new HsParticleSystem();
    smokeParticlesWithCollision.setGeometry(spriteGeometry);
    smokeParticlesWithCollision.setMaterial(texturedPlainMaterial);
    smokeParticlesWithCollision.setQueue(this.objectsQueue);
    smokeParticlesWithCollision.setEmitter(new HsSphereEmitter(0, 0, 0, 0.5));
    smokeParticlesWithCollision.worldMatrix.setPosition([0, 0, -60]);
    smokeParticlesWithCollision.size = 0.5;
    smokeParticlesWithCollision.scattering = 0.02;
    smokeParticlesWithCollision.sizeVariation = 1;
    smokeParticlesWithCollision.setForces([antiGravity]);

    const sphereCollider: HsSphereCollider = new HsSphereCollider(0, 10, -60, 2);
    sphereCollider.bounciness = 0.2;
    smokeParticlesWithCollision.setColliders([sphereCollider]);

    const snow: HsParticleSystem = new HsParticleSystem();
    snow.setGeometry(spriteGeometry);
    snow.setMaterial(snowMaterial);
    snow.setQueue(this.objectsQueue);
    snow.setEmitter(new HsBoxEmitter(0, 0, 0, 20, 1, 20));
    snow.worldMatrix.setPosition([0, 20, -100]);
    snow.size = 0.1;
    snow.scattering = 0.02;
    snow.sizeVariation = 1;
    snow.setForces([gravity, wind]);

    this.particleSystems.push(smokeParticles, smokeParticlesWithCollision, snow);

    this.viewer?.invalidate();
  }

  public addSingleObjects(): void {
    if (this.gl === null || this.graphics === null || this.objectsQueue === null) {
      return;
    }
    this.particleSystems = [];
    this.distributedRendering = true;
    this.objectsQueue.clear(this.graphics);
    this.cameraPoint.set(200, 10, 0);
    this.observablePoint.set(0, 0, 0);
    this.camera.worldMatrix.setPosition(this.cameraPoint.values);
    this.observablePoint.set(0, 8, 0);
    HsHypersceneMathUtils.lookAt(
      this.camera.worldMatrix,
      new HsVector3(...this.cameraPoint.values),
      this.observablePoint,
      HsHypersceneEngineConstants.UP
    );
    const glassShaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
    glassShaderData.addColor().addDepth().addNormals().addRayIntersection().addDiffuseLighting();
    const glassShader: HsShader = glassShaderData.makeShader();
    const glassMaterial: HsMaterial = new HsDefaultMaterial(this.gl, glassShader);
    glassMaterial.color.set(1.0, 0.75, 0.5);
    const indexedData: HsIndexedGeometryData = hsIndexGeometry(hsGenerateSphereGeometryData(60));
    const objectGeometry: HsGeometry | null = hsMakeIndexedGeometry(this.graphics, indexedData);
    if (objectGeometry === null) {
      return;
    }
    const objectsCount: number = 20;
    for (let i: number = 0; i < objectsCount; i++) {
      for (let j: number = 0; j < objectsCount; j++) {
        for (let k: number = 0; k < objectsCount; k++) {
          const x: number = i * 3 - objectsCount * 1.5;
          const y: number = j * 3 - objectsCount * 1.5;
          const z: number = k * 3 - objectsCount * 1.5;
          const object: HsGraphicObject = new HsGraphicObject(getUuid(), 'object', objectGeometry, glassMaterial);
          object.worldMatrix.setPosition([x, y, z]);
          this.objectsQueue.addObject(object);
          object.updateBoundingSphere();
        }
      }
    }
    const cubeData: HsRegularGeometryData = hsGenerateCubeGeometryData();
    const cubeIndexedData: HsIndexedGeometryData = hsIndexGeometry(cubeData);
    const cubeGeometry: HsGeometry | null = hsMakeIndexedGeometry(this.graphics, cubeIndexedData);
    if (cubeGeometry === null) {
      return;
    }
    const cube: HsGraphicObject = new HsGraphicObject(getUuid(), 'object', cubeGeometry, glassMaterial);
    cube.worldMatrix.setPosition([100, 0, 0]).scale([10, 10, 10]);
    this.objectsQueue.addObject(cube);
    cube.updateBoundingSphere();
  }

  public toggleXRay(): void {
    if (this.gl === null) {
      return;
    }
    this.xRayMode = !this.xRayMode;

    if (this.xRayMode) {
      const xRayShaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
      xRayShaderData.addColor().addDepth().addTransparency(0.15);
      const xRayShader: HsShader = xRayShaderData.makeShader();
      this.xRayMaterial = new HsDefaultMaterial(this.gl, xRayShader);
      this.xRayMaterial.color.set(0, 0, 0);
    } else {
      const solidShaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
      solidShaderData
        .addColor()
        .addDepth()
        .addNormals()
        .addRayIntersection()
        .addDiffuseLighting()
        .addBackgroundReflection()
        .addFresnel();
      const solidShader: HsShader = solidShaderData.makeShader();
      const solidMaterial: HsMaterial = new HsDefaultMaterial(this.gl, solidShader);
      solidMaterial.color.set(0.5, 0.5, 0.5);
      this.xRayMaterial = solidMaterial;
    }
    const xRayMaterial: HsMaterial = this.xRayMaterial;
    this.xRayObjects.forEach((object: HsGraphicObject) => {
      object.material = xRayMaterial;
    });
  }

  public addXRayObjects(): void {
    if (this.gl === null || this.graphics === null || this.objectsQueue === null || this.xRayMaterial === null) {
      return;
    }
    this.particleSystems = [];
    this.xRayObjects.length = 0;
    this.distributedRendering = false;
    this.objectsQueue.clear(this.graphics);
    this.cameraPoint.set(200, 10, 0);
    this.observablePoint.set(0, 0, 0);
    this.camera.worldMatrix.setPosition(this.cameraPoint.values);
    this.observablePoint.set(0, 8, 0);
    HsHypersceneMathUtils.lookAt(
      this.camera.worldMatrix,
      new HsVector3(...this.cameraPoint.values),
      this.observablePoint,
      HsHypersceneEngineConstants.UP
    );
    const capsuleData: HsRegularGeometryData = hsGenerateCapsuleGeometryData(12);
    const cylinderData: HsRegularGeometryData = hsGenerateCylinderGeometryData(8, true, true);
    const capsuleMatrix: HsMatrix4 = HsMatrix4.makeIdentityMatrix()
      .rotateX(Math.PI / 2)
      .scale([3, 3, 3]);
    const cylinderMatrix: HsMatrix4 = HsMatrix4.makeIdentityMatrix().scale([0.5, 0.1, 0.5]).addTransition([0, 3, 0]);
    const meshData: HsRegularGeometryData = hsMergeGeometries([
      [capsuleData, capsuleMatrix],
      [cylinderData, cylinderMatrix],
    ]);
    const indexedData: HsIndexedGeometryData = hsIndexGeometry(meshData);
    const objectGeometry: HsGeometry | null = hsMakeIndexedGeometry(this.graphics, indexedData);
    if (objectGeometry === null) {
      return;
    }
    const cubeData: HsRegularGeometryData = hsGenerateCubeGeometryData();
    const cubeIndexedData: HsIndexedGeometryData = hsIndexGeometry(cubeData);
    const cubeGeometry: HsGeometry | null = hsMakeIndexedGeometry(this.graphics, cubeIndexedData);
    if (cubeGeometry === null) {
      return;
    }
    const objectsCount: number = 50;
    for (let i: number = 0; i < objectsCount; i++) {
      for (let j: number = 0; j < objectsCount; j++) {
        const x: number = i * 20 - objectsCount * 3;
        const y: number = j * 20 - objectsCount * 3;
        const object: HsGraphicObject = new HsGraphicObject(getUuid(), 'object', objectGeometry, this.xRayMaterial);
        object.worldMatrix.setPosition([x, 0, y]);
        this.objectsQueue.addObject(object);
        object.updateBoundingSphere();
        this.xRayObjects.push(object);
        if (Math.random() < 0.1) {
          const cube: HsGraphicObject = new HsGraphicObject(getUuid(), 'object', cubeGeometry, this.xRayMaterial);
          cube.worldMatrix.setPosition([x, 0, y]);
          this.objectsQueue.addObject(cube);
          cube.updateBoundingSphere();
          this.xRayObjects.push(cube);
        }
      }
    }
    this.viewer?.invalidate();
  }

  public add500k(): void {
    if (this.gl === null || this.graphics === null || this.objectsQueue === null) {
      return;
    }
    this.particleSystems = [];
    this.distributedRendering = false;
    this.objectsQueue.clear(this.graphics);
    this.cameraPoint.set(400, 150, 400);
    this.observablePoint.set(0, -30, 0);
    this.camera.worldMatrix.setPosition(this.cameraPoint.values);
    const cubeData: HsRegularGeometryData = hsGenerateCubeGeometryData();
    const cubes: [HsRegularGeometryData, HsMatrix4][] = [];
    const batchSize: number = 8;
    for (let i: number = 0; i < batchSize; i++) {
      for (let j: number = 0; j < batchSize; j++) {
        for (let k: number = 0; k < batchSize; k++) {
          const worldMatrix: HsMatrix4 = HsMatrix4.makeIdentityMatrix();
          worldMatrix.setPositionX(i * 3);
          worldMatrix.setPositionY(j * 3);
          worldMatrix.setPositionZ(k * 3);
          cubes.push([cubeData, worldMatrix]);
        }
      }
    }
    const batch: HsRegularGeometryData = hsMergeGeometries(cubes);
    const indexedCubeData: HsIndexedGeometryData = hsIndexGeometry(batch);
    const cubeGeometry: HsGeometry | null = hsMakeIndexedGeometry(this.graphics, indexedCubeData);
    const metalShaderData: HsShaderData = new HsShaderData(HsShaderObjectType.collection);
    metalShaderData
      .addColor()
      .addDepth()
      .addNormals()
      .addDiffuseLighting()
      .addRayIntersection()
      .addBackgroundReflection()
      .addFresnel();
    const metalShader: HsShader = metalShaderData.makeShader();
    const metalMaterial: HsMaterial = new HsDefaultMaterial(this.gl, metalShader);
    metalMaterial.color.set(0.5, 0.5, 0.5);
    if (cubeGeometry !== null) {
      const collection: HsGraphicCollection = new HsGraphicCollection(getUuid(), '', cubeGeometry, metalMaterial);
      const drawCubeSize: number = 10;
      for (let i: number = 0; i < drawCubeSize; i++) {
        for (let j: number = 0; j < drawCubeSize; j++) {
          for (let k: number = 0; k < drawCubeSize; k++) {
            const cube: HsGraphicObject = new HsGraphicObject(getUuid(), 'cube', cubeGeometry, metalMaterial);
            cube.worldMatrix.setPositionX(i * 3 * batchSize - drawCubeSize * 1.5 * batchSize);
            cube.worldMatrix.setPositionY(j * 3 * batchSize - drawCubeSize * 1.5 * batchSize);
            cube.worldMatrix.setPositionZ(k * 3 * batchSize - drawCubeSize * 1.5 * batchSize);
            collection.addObject(cube);
          }
        }
      }
      collection.updateCenter();
      collection.updateBoundingSphere();
      collection.updateBuffers(this.graphics);
      this.objectsQueue.addObject(collection);
    }
    this.viewer?.invalidate();
  }

  private get render(): HsRender {
    return (this.viewer as HsHypersceneViewer).render;
  }

  private set distributedRendering(value: boolean) {
    if (this.viewer !== null) {
      this.viewer.distributedRendering = value;
    }
  }

  private get controlActionsHandler(): HsControlActionsHandler | null {
    return this.viewer?.controls ?? null;
  }

  private initOptions(canvas: HTMLCanvasElement): void {
    // The page draws every frame (particles, FPS counter) and resizes its own HUD layers.
    const viewer: HsHypersceneViewer = new HsHypersceneViewer({
      canvas,
      camera: this.camera,
      continuous: true,
      autoResize: false,
    });
    this.viewer = viewer;
    // Left drags are left to the page's tools; middle and right drags pan and orbit.
    this.viewerInput = new HsViewerInput(viewer, {
      dragActions: [null, HsControlActionType.MiddleMove, HsControlActionType.RightMove],
    });
    this.gl = viewer.gl;
    this.graphics = viewer.surface.graphics;
    this.objectsLayer = viewer.surface.objectsLayer;
    this.objectsQueue = viewer.surface.objectsQueue;
    this.canvasSize = viewer.surface.canvasSize;

    this.cubeQueue = new HsGraphicQueue(getUuid(), new HsGraphicLayer(this.cubeCamera, viewer.gl, 4));
    this.axisQueue = new HsGraphicQueue(getUuid(), new HsGraphicLayer(this.camera, viewer.gl, 4));
    this.setLayerSizes(viewer.surface.width, viewer.surface.height);
    this.cubeCamera.aspect = viewer.surface.width / viewer.surface.height;
    viewer.onFrame(() => this.update());

    window.addEventListener('resize', this.handleWindowResize);
  }

  private setLayerSizes(width: number, height: number): void {
    if (this.gl === null) {
      return;
    }
    this.cubeLayer = new HsGraphicLayer(this.cubeCamera, this.gl, 4);
    this.controlCubeRenderObjects = new HsRenderObjects(this.cubeLayer);
    const cubeLayerX: number = width - width / 8;
    const cubeLayerY: number = height - height / 4;
    const cubeLayerWidth: number = width;
    const cubeLayerHeight: number = height;
    this.cubeLayer.setRect(cubeLayerX, cubeLayerY, 0.3);
    this.cubeLayer.resizeFramebuffer(this.gl, cubeLayerWidth, cubeLayerHeight);

    this.axisLayer = new HsGraphicLayer(this.cubeCamera, this.gl, 4);
    this.axisRenderObjects = new HsRenderObjects(this.axisLayer);
    const axisLayerX: number = -width + width / 8;
    const axisLayerY: number = height - height / 4;
    const axisLayerWidth: number = width;
    const axisLayerHeight: number = height;
    this.axisLayer.setRect(axisLayerX, axisLayerY, 0.3);
    this.axisLayer.resizeFramebuffer(this.gl, axisLayerWidth, axisLayerHeight);
  }

  private initScene(): void {
    if (this.gl === null) {
      return;
    }

    this.camera.fov = 1;
    this.camera.worldMatrix.setPosition(CAMERA_POSITION);
    this.camera.updateProjectionMatrix();
    this.camera.updateLocalMatrix();

    this.cubeCamera.fov = 0.7;
    this.cubeCamera.worldMatrix.setPosition([0, 0, 5]);
    this.cubeCamera.updateProjectionMatrix();
    this.cubeCamera.updateLocalMatrix();

    this.addAllObjects();

    if (this.objectsLayer === null) {
      return;
    }

    this.raycast.layers = [this.objectsLayer];

    this.selectedCameraToolType = this.viewer?.cameraTools.activeCameraToolType ?? null;

    const solidShaderData: HsShaderData = new HsShaderData(HsShaderObjectType.single);
    solidShaderData
      .addColor()
      .addDepth()
      .addNormals()
      .addRayIntersection()
      .addDiffuseLighting()
      .addBackgroundReflection()
      .addFresnel();
    const solidShader: HsShader = solidShaderData.makeShader();
    const solidMaterial: HsMaterial = new HsDefaultMaterial(this.gl, solidShader);
    solidMaterial.color.set(0.5, 0.5, 0.5);
    this.xRayMaterial = solidMaterial;
    this.viewer?.invalidate();
  }

  /** Runs after the viewer drew the scene: particles, the control cube and axis HUD, and the FPS counter. */
  private update(): void {
    if (
      this.gl === null ||
      this.controlCubeRenderObjects === null ||
      this.axisRenderObjects === null ||
      this.cubeQueue === null ||
      this.axisQueue === null
    ) {
      return;
    }
    const gl: WebGL2RenderingContext = this.gl;

    this.particleSystems.forEach((particleSystem: HsParticleSystem) => {
      particleSystem.update();
    });

    this.cubeCamera.worldMatrix.copyRotation(this.camera.worldMatrix);
    this.cubeCamera.updateProjectionMatrix();
    this.cubeCamera.updateCameraMatrixInverse();

    const cubeCameraRotation: HsMatrix3 = HsMatrix3.makeIdentityMatrix()
      .setFromMatrix4(this.camera.worldMatrix.values)
      .inverse();
    const cubeCameraPosition: HsVector3 = HsVector3.makeUnitVectorZ().applyMatrix3(cubeCameraRotation.values);
    cubeCameraPosition.multiplyByScalar(6);
    this.cubeCamera.worldMatrix.setPosition(cubeCameraPosition.values);

    this.controlCubeRenderObjects.clear();
    HS_DEFAULT_VIEWER_FILTER.filter(this.cubeQueue, this.cubeCamera, this.controlCubeRenderObjects);
    this.axisRenderObjects.clear();
    HS_DEFAULT_VIEWER_FILTER.filter(this.axisQueue, this.cubeCamera, this.axisRenderObjects);
    this.render.drawToFramebuffer(gl, this.cubeCamera, this.controlCubeRenderObjects);
    this.render.drawToFramebuffer(gl, this.cubeCamera, this.axisRenderObjects);

    this.frame++;
    if (performance.now() - this.time > 1000) {
      this.time = performance.now();
      this.fps = this.frame;
      this.changeDetector.markForCheck();
      this.frame = 0;
    }
  }

  private handleKeydown(): Subscription {
    return fromEvent(this.document, 'keydown')
      .pipe(subscribeOutsideAngular(this.ngZone))
      .subscribe((event: Event) => this.setKeyboardAction(event, true));
  }

  private handleKeyup(): Subscription {
    return fromEvent(this.document, 'keyup')
      .pipe(subscribeOutsideAngular(this.ngZone))
      .subscribe((event: Event) => this.setKeyboardAction(event, false));
  }

  private handleOutputMessage(): HsSubscriberData<HsOutputMessageObject> {
    return HsGlobal.outputMessages.subscribe((outputMessageObject: HsOutputMessageObject) => {
      if (
        outputMessageObject.type === HsOutputMessageType.CameraLookAtComplete ||
        outputMessageObject.type === HsOutputMessageType.CameraFocusAtComplete
      ) {
        this.selectedCameraToolType = HsCameraToolType.Default;
        const cameraToolTypeControlAction: HsCameraToolTypeControlAction = new HsCameraToolTypeControlAction(
          this.selectedCameraToolType
        );
        this.controlActionsHandler?.updateControlAction(cameraToolTypeControlAction);
      }
    });
  }

  private setKeyboardAction(event: Event, isActive: boolean): void {
    if (this.controlActionsHandler === null) {
      return;
    }

    if (!(event instanceof KeyboardEvent)) {
      return;
    }

    if (!this.isViewerCanvasFocused()) {
      return;
    }

    const actionType: HsControlActionType | undefined = KEY_MAP.get(event.key);
    if (actionType === undefined) {
      return;
    }

    if (isActive && actionType === HsControlActionType.PressedShift && !this.shiftHasPressed) {
      this.setRotationCenter();
      this.shiftHasPressed = true;
    }

    if (!isActive && actionType === HsControlActionType.PressedShift && this.shiftHasPressed) {
      HsGlobal.ROTATE_POINT.setFromArray([0, 2, 0]);
      this.shiftHasPressed = false;
    }

    const keyboardControlAction: HsKeyboardControlAction = new HsKeyboardControlAction(actionType, isActive);
    this.controlActionsHandler.updateControlAction(keyboardControlAction);
  }

  private isViewerCanvasFocused(): boolean {
    const canvas: HTMLCanvasElement | undefined = this.canvasDocument?.nativeElement;
    return canvas !== undefined && this.document.activeElement === canvas;
  }

  private setRotationCenter(): void {
    if (this.canvasSize === null) {
      return;
    }

    const canvasSize: HsCanvasSize = this.canvasSize;

    const deviceCoordinateX: number =
      ((this.currentPointerPosition.x - canvasSize.left) / canvasSize.renderWidth) * 2 - 1;
    const deviceCoordinateY: number =
      1 - ((this.currentPointerPosition.y - canvasSize.top) / canvasSize.renderHeight) * 2;

    const rotationPoint: HsVector3 = HsVector3.makeZeroVector().setFromMatrixPosition(this.camera.worldMatrix.values);
    const direction: HsVector3 = HsVector3.makeZeroVector()
      .set(deviceCoordinateX, deviceCoordinateY, 0.5)
      .unproject(this.camera.projectionMatrixInverse.values, this.camera.worldMatrix.values)
      .subtract(rotationPoint)
      .normalize()
      .multiplyByScalar(ROTATION_POINT_DISTANCE);

    rotationPoint.add(direction);

    HsGlobal.ROTATE_POINT.copy(rotationPoint);
  }

  private resize(): void {
    if (this.viewer === null || this.gl === null || this.cubeLayer === null || this.axisLayer === null) {
      return;
    }
    this.viewer.resize();
    const { width, height }: HsViewerSurface = this.viewer.surface;
    this.cubeCamera.aspect = width / height;
    this.render.setRenderSize(this.gl, width, height, [this.cubeLayer, this.axisLayer]);
  }
}
